import { entityPaths } from '@lakaran/geometry';
import { trimEdges, type NodeGraph } from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';
import {
  createPieceHighlighter,
  createPiecePicker,
  type PickPiece,
  type PieceHighlighter,
  type PiecePicker,
} from './piece-picker';

/**
 * Trim picked in the drawing.
 *
 * A trim step keeps every span it produced; the node stores only the keys the user cut away, so
 * the picker is an overlay of the pieces themselves: hover highlights one, clicking toggles it,
 * and `X` / `Esc` / right-click ends the mode. Nothing is ever destroyed — "restore" on the card
 * clears the list.
 */

export interface TrimPickerOptions {
  graph: () => NodeGraph;
  /** Reverses the removed state of one piece and repaints. */
  onToggle: (nodeId: string, key: string, remove: boolean) => void;
  onEnd?: () => void;
}

export type TrimPicker = PiecePicker;
export type TrimHighlighter = PieceHighlighter;

export function createTrimPicker(
  editor: CadEditor,
  host: HTMLElement,
  options: TrimPickerOptions,
): TrimPicker {
  const pieces = (nodeId: string): PickPiece[] =>
    trimEdges(options.graph(), nodeId, editor.doc.currentLayerId).map((edge) => ({
      key: edge.key,
      label: edge.label,
      // A removed span is the state this mode paints, and clicking it puts the span back.
      on: !edge.kept,
      paths: entityPaths(edge.entity),
    }));

  return createPiecePicker(editor, host, {
    pieces,
    onToggle: (nodeId, key, remove) => options.onToggle(nodeId, key, remove),
    hint: 'Trim — click a piece to cut it away, click again to restore, X or Esc ends',
    startMessage: (count) =>
      `TRIM  ${count} edges in this step — click the ones to remove (X ends).`,
    emptyMessage: 'TRIM  Nothing to trim — link a stream into the trim step first.',
    action: (piece) => (piece.on ? 'click to restore' : 'click to trim'),
    onClass: 'is-removed',
    onEnd: options.onEnd,
  });
}

/**
 * A lightweight version of the trim overlay used by the card's list: hovering a row lights up the
 * pieces it removed, without arming the picker.
 */
export function createTrimHighlighter(
  editor: CadEditor,
  host: HTMLElement,
  options: { graph: () => NodeGraph },
): TrimHighlighter {
  return createPieceHighlighter(editor, host, {
    pieces: (nodeId) =>
      trimEdges(options.graph(), nodeId, editor.doc.currentLayerId).map((edge) => ({
        key: edge.key,
        label: edge.label,
        on: !edge.kept,
        paths: entityPaths(edge.entity),
      })),
    hotClass: 'is-removed',
  });
}
